import { useState, useEffect, useMemo, useCallback } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { useBrand } from "@/hooks/useBrand";
import { useDriverLocation } from "@/hooks/useDriverLocation";
import { usePermissions } from "@/contexts/PermissionContext";
import { useDataFiltering } from "@/hooks/useDataFiltering";
import { MapComponent } from "@/components/map/MapComponent";
import { MapControls } from "@/components/map/MapControls";
import { DriverWithLocation } from "@/components/map/DriverInfoWindow";
import { MapAccessControl } from "@/components/access-control/MapAccessControl";
import { GOOGLE_MAPS_OPTIONS } from "@/lib/googleMapsConfig";
import { Card } from "@/components/atoms/card";
import LoadingSpinner from "@/components/orders/LoadingSpinner";
import ErrorMessage from "@/components/orders/ErrorMessage";
import OrderSheet from "@/components/modals/OrderSheet";
import DriverProfileModal from "@/components/modals/DriverProfileModal";
import { DRIVER_STATUS } from "@/lib/statusConfig";
import type { Branch } from "@/components/map/MapComponent";
import type { Brand } from "@/components/map/MapControls";
import { PageHeader } from "@/components/PageHeader";

// --- MQTT subscribing state helper ---
function useMqttSubscribing(
  loadingDrivers: boolean,
  mqttConnected: boolean,
  realTimeDrivers: Record<string, any>,
) {
  // "Subscribing" is when loadingDrivers is true, mqttConnected is false, and we have not yet received any data
  return (
    loadingDrivers &&
    !mqttConnected &&
    Object.keys(realTimeDrivers).length === 0
  );
}

const createMapIcon = (url: string, size: number = 40): google.maps.Icon => ({
  url,
  scaledSize: new google.maps.Size(size, size),
  anchor: new google.maps.Point(size / 2, size / 2),
});

const Map = () => {
  // Google Maps API loading
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_OPTIONS);

  // Permission and filtering hooks
  const { hasPermission, hasBrandAccess, hasBranchAccess } = usePermissions();
  const { filterBrands, filterBranches, filterDrivers } = useDataFiltering();

  // Data hooks
  const { brands: allBrands, loading, error } = useBrand();

  // Filter brands based on user permissions
  const brands = useMemo(() => {
    if (!allBrands) return [];

    // Check if user has map access
    const hasMapAccess =
      hasPermission("map:view_full") || hasPermission("map:view_partial");
    if (!hasMapAccess) return [];

    // Filter brands based on business access
    const filteredBrands = filterBrands(allBrands);

    // Also filter branches and drivers within each brand
    return filteredBrands.map((brand) => {
      const filteredBranches = brand.branches
        ? filterBranches(brand.branches)
        : [];

      // Filter drivers within each branch
      const branchesWithFilteredDrivers = filteredBranches.map((branch) => {
        const filteredDriversInBranch = branch.drivers
          ? filterDrivers(branch.drivers)
          : [];
        return {
          ...branch,
          drivers: filteredDriversInBranch,
        };
      });

      return {
        ...brand,
        branches: branchesWithFilteredDrivers,
      };
    });
  }, [allBrands, hasPermission, filterBrands, filterBranches, filterDrivers]);

  // State management
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [showDrivers, setShowDrivers] = useState(true);
  const [showServiceZone, setShowServiceZone] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] =
    useState<DriverWithLocation | null>(null);
  const [info, setInfo] = useState<{
    type: "branch" | "driver";
    data: any;
  } | null>(null);

  // Driver location hook
  const { realTimeDrivers, mqttConnected, loadingDrivers } = useDriverLocation({
    selectedBranchId,
    selectedBrandId,
    isMapLoaded: isLoaded,
    brandsLength: brands.length,
  });
  // MQTT subscribing state
  const mqttSubscribing = useMqttSubscribing(
    loadingDrivers,
    mqttConnected,
    realTimeDrivers,
  );

  // Map icons - memoized to prevent unnecessary recreations
  const mapIcons = useMemo(() => {
    if (!isLoaded) return null;

    return {
      branch: createMapIcon("/branch-map.png", 40),
      driver: createMapIcon("/driver-gray.png", 40),
      availableDriver: createMapIcon("/driver-free.png", 40),
    };
  }, [isLoaded]);

  // Computed values with permission checks
  const selectedBrand = useMemo(() => {
    return brands.find((b) => b.id.toString() === selectedBrandId);
  }, [brands, selectedBrandId]);

  const branches = useMemo(() => {
    if (!selectedBrand) return [];
    return selectedBrand.branches || [];
  }, [selectedBrand]);

  const selectedBranch = useMemo(() => {
    const branch = branches.find((br) => br.id.toString() === selectedBranchId);
    return branch as unknown as Branch;
  }, [branches, selectedBranchId]);

  const drivers = useMemo(() => {
    if (!selectedBranch) return [];
    return selectedBranch.drivers || [];
  }, [selectedBranch]);

  // Filter drivers with valid coordinates (excluding 0,0)
  const validDrivers = useMemo(() => {
    return drivers.filter((driver) => {
      const realTimeData = realTimeDrivers[driver.id];

      const location = realTimeData
        ? { lat: realTimeData.lat, lng: realTimeData.lng }
        : { lat: driver.location_lat, lng: driver.location_lon };

      // Use same validation logic as MapComponent
      const isValidCoordinate = (lat?: number, lng?: number) =>
        typeof lat === "number" &&
        typeof lng === "number" &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180 &&
        !(lat === 0 && lng === 0);

      return isValidCoordinate(location.lat, location.lng);
    });
  }, [drivers, realTimeDrivers]);

  const branchCenter = useMemo(() => {
    if (
      selectedBranch &&
      typeof selectedBranch.location_lat === "number" &&
      typeof selectedBranch.location_lon === "number" &&
      selectedBranch.location_lat >= -90 &&
      selectedBranch.location_lat <= 90 &&
      selectedBranch.location_lon >= -180 &&
      selectedBranch.location_lon <= 180
    ) {
      return {
        lat: selectedBranch.location_lat,
        lng: selectedBranch.location_lon,
      };
    }
    return { lat: 14.6136403, lng: -90.5314688 }; // Default center
  }, [selectedBranch]);

  // Auto-select first accessible brand and branch on load
  useEffect(() => {
    if (Array.isArray(brands) && brands.length > 0 && !selectedBrandId) {
      const firstBrand = brands[0];
      // Check if user has access to this brand
      if (hasBrandAccess(firstBrand.id)) {
        setSelectedBrandId(firstBrand.id.toString());
        const firstBranch = firstBrand.branches?.[0];
        if (firstBranch && hasBranchAccess(firstBranch.id)) {
          setSelectedBranchId(firstBranch.id.toString());
        }
      }
    }
  }, [brands, selectedBrandId, hasBrandAccess, hasBranchAccess]);

  // Event handlers with permission checks
  const handleBrandChange = useCallback(
    (id: string) => {
      const brand = brands.find((b) => b.id.toString() === id);
      if (brand && hasBrandAccess(brand.id)) {
        setSelectedBrandId(id);
        const firstBranch = brand.branches?.[0];
        if (firstBranch && hasBranchAccess(firstBranch.id)) {
          setSelectedBranchId(firstBranch.id.toString());
        } else {
          setSelectedBranchId("");
        }
        setInfo(null);
      }
    },
    [brands, hasBrandAccess, hasBranchAccess],
  );

  const handleBranchChange = useCallback(
    (branchId: string) => {
      const branch = branches.find((b) => b.id.toString() === branchId);
      if (branch && hasBranchAccess(branch.id)) {
        setSelectedBranchId(branchId);
        setInfo(null);
      }
    },
    [branches, hasBranchAccess],
  );

  const handleToggleDrivers = useCallback(() => {
    setShowDrivers((prev) => !prev);
  }, []);

  const handleToggleServiceZone = useCallback(() => {
    setShowServiceZone((prev) => !prev);
  }, []);

  const handleBranchClick = useCallback(
    (branch: Branch) => {
      // Check if user has access to this branch
      if (hasBranchAccess(branch.id)) {
        setInfo({ type: "branch", data: branch });
      }
    },
    [hasBranchAccess],
  );

  // Function to show InfoWindow for the currently selected branch
  const showSelectedBranchInfo = useCallback(() => {
    const currentBranch = branches.find(
      (b) => b.id.toString() === selectedBranchId,
    );

    if (currentBranch && hasBranchAccess(currentBranch.id)) {
      setInfo({ type: "branch", data: currentBranch });
    }
  }, [selectedBranchId, branches, hasBranchAccess]);

  const handleDriverClick = useCallback(
    (driverData: any) => {
      // Check if user has access to the driver's branch
      // Since drivers are displayed in the context of selectedBranchId, use that for permission check
      if (selectedBranchId && hasBranchAccess(Number(selectedBranchId))) {
        setInfo(driverData);
      }
    },
    [hasBranchAccess, selectedBranchId],
  );

  const handleInfoWindowClose = useCallback(() => {
    setInfo(null);
  }, []);

  const handleProfileClick = useCallback(
    async (driver: DriverWithLocation) => {
      // Check if user has access to this driver's branch
      // Since the driver is displayed in the context of selectedBranchId, use that for permission check
      if (selectedBranchId && hasBranchAccess(Number(selectedBranchId))) {
        const realTimeDriver = realTimeDrivers[driver.id.toString()];

        const driverData = realTimeDriver
          ? { ...driver, ...realTimeDriver }
          : { ...driver, status: DRIVER_STATUS.OFFLINE };

        setSelectedDriver(driverData);
        setIsProfileModalOpen(true);
      }
    },
    [realTimeDrivers, hasBranchAccess, selectedBranchId],
  );

  const [isTestMode, setIsTestMode] = useState(false);

  const handleOrderClick = useCallback((payload: any) => {
    // payload can be a string orderId or an object { orderId, realTime }
    if (typeof payload === "string") {
      setSelectedOrderIds([payload]);
      setIsTestMode(false);
    } else if (payload && typeof payload === "object") {
      setSelectedOrderIds(payload.orderIds);
      setIsTestMode(payload.isTestMode || false);
    }
    setIsOrderModalOpen(true);
  }, []);

  const handleNavigateDriver = useCallback(
    (direction: "prev" | "next") => {
      if (!info?.data?.drivers || !Array.isArray(info.data.drivers)) return;

      const currentIndex = info.data.index || 0;
      const driversCount = info.data.drivers.length;

      let newIndex;
      if (direction === "prev") {
        newIndex = currentIndex <= 0 ? driversCount - 1 : currentIndex - 1;
      } else {
        newIndex = currentIndex >= driversCount - 1 ? 0 : currentIndex + 1;
      }

      setInfo({
        ...info,
        data: {
          ...info.data,
          index: newIndex,
        },
      });
    },
    [info],
  );

  // Loading and error states
  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!isLoaded || !mapIcons) return <div>Loading map...</div>;

  // Check if user has map access
  const hasMapAccess =
    hasPermission("map:view_full") || hasPermission("map:view_partial");
  if (!hasMapAccess) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Acceso Restringido
          </h2>
          <p className="text-gray-600">
            No tienes permisos para acceder al mapa. Contacta a tu administrador
            para obtener acceso.
          </p>
        </div>
      </div>
    );
  }

  if (!brands || brands.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Sin Acceso a Negocios
          </h2>
          <p className="text-gray-600">
            No tienes acceso a ningún negocio. Contacta a tu administrador para
            obtener acceso.
          </p>
        </div>
      </div>
    );
  }

  // Overlay conditions
  const showOverlay = () => {
    if (!selectedBranchId) {
      return (
        <div className="absolute inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <p className="text-gray-700">
              Selecciona una sucursal para ver los conductores en el mapa.
            </p>
          </div>
        </div>
      );
    }
    // Show loading overlay if MQTT is subscribing
    if (mqttSubscribing) {
      return (
        <div className="absolute inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <LoadingSpinner />
            <p className="text-gray-700 mt-2">
              Suscribiéndose a actualizaciones en tiempo real…
            </p>
          </div>
        </div>
      );
    }
    // Only show loading overlay if we're still loading drivers AND we don't have any data yet
    if (loadingDrivers && Object.keys(realTimeDrivers).length === 0) {
      return (
        <div className="absolute inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <LoadingSpinner />
            <p className="text-gray-700 mt-2">Cargando conductores…</p>
          </div>
        </div>
      );
    }
    // Only show connection overlay if we're not connected AND we don't have any data yet
    if (!mqttConnected && Object.keys(realTimeDrivers).length === 0) {
      return (
        <div className="absolute inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <LoadingSpinner />
            <p className="text-gray-700 mt-2">Conectando al servidor…</p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Show message at bottom left when no valid drivers are assigned (excluding those with coordinates 0,0)
  const showNoDriversMessage =
    selectedBranchId &&
    !loadingDrivers &&
    mqttConnected &&
    validDrivers.length === 0;

  return (
    <MapAccessControl brandId={selectedBrand?.id} branchId={selectedBranch?.id}>
      <div className="min-h-screen bg-gray-100">
        {/* Header */}
        <PageHeader
          title="Mapa"
          description="Seguimiento de conductores, órdenes y sucursales"
        />

        <div className="p-4 md:p-6 max-w-7xl mx-auto">
          <Card className="bg-white overflow-hidden relative">
            {/* Map Controls */}
            <div className="p-4 border-b border-gray-200">
              <MapControls
                brands={brands as Brand[]}
                selectedBrandId={selectedBrandId}
                selectedBranchId={selectedBranchId}
                showDrivers={showDrivers}
                showServiceZone={showServiceZone}
                onBrandChange={handleBrandChange}
                onBranchChange={handleBranchChange}
                onToggleDrivers={handleToggleDrivers}
                onToggleServiceZone={handleToggleServiceZone}
                onShowBranchInfo={showSelectedBranchInfo}
              />
            </div>

            {/* Map Container */}
            <div className="h-[calc(100vh-16rem)] relative">
              {showOverlay()}

              {/* No drivers message at bottom left */}
              {showNoDriversMessage && (
                <div className="absolute bottom-4 left-4 z-40 bg-white p-3 rounded-lg shadow-lg border border-gray-200 max-w-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <p className="text-sm text-gray-700 font-medium">
                      No hay conductores con ubicación válida en esta sucursal
                    </p>
                  </div>
                </div>
              )}

              <MapComponent
                center={branchCenter}
                selectedBranch={selectedBranch}
                selectedBrandName={selectedBrand?.name || ""}
                selectedBrandId={selectedBrandId}
                drivers={drivers}
                realTimeDrivers={realTimeDrivers}
                showDrivers={showDrivers}
                showServiceZone={showServiceZone}
                branchIcon={mapIcons.branch}
                driverIcon={mapIcons.driver}
                availableDriverIcon={mapIcons.availableDriver}
                info={info}
                onBranchClick={handleBranchClick}
                onDriverClick={handleDriverClick}
                onInfoWindowClose={handleInfoWindowClose}
                onProfileClick={handleProfileClick}
                onOrderClick={handleOrderClick}
                onNavigateDriver={handleNavigateDriver}
              />
            </div>
          </Card>
        </div>

        {/* Modals */}
        <OrderSheet
          isOpen={isOrderModalOpen}
          onClose={() => setIsOrderModalOpen(false)}
          orderIds={selectedOrderIds}
          isTestMode={isTestMode}
        />

        <DriverProfileModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          driver={selectedDriver}
        />
      </div>
    </MapAccessControl>
  );
};

export default Map;
